import { flatMap } from 'lodash';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  CopyBeneficiaryDesignationsOptionsQuery,
  useCopyBeneficiaryDesignationsOptionsQuery,
} from '@/modules/beneficiaryDesignations/graphql/BeneficiaryDesignations.generated';
import { mapProvisionToRecipient } from '@/modules/dispositiveProvisions/dispositiveProvisions.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { BeneficiaryDesignationTier } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { CopyBeneficiaryDesignationsResult } from './CopyBeneficiaryDesignationsModal.types';

/**
 * The two account subtypes that carry beneficiary designations. Copy sources
 * are restricted to the same subtype as the current entity (retirement copies
 * only from retirement, insurance only from insurance) per product decision.
 */
type BdgAccountTypename =
  | 'RetirementPersonalAccount'
  | 'InsurancePersonalAccount';

function getSubtypeTypename(entityType: EntityType): BdgAccountTypename {
  return entityType === 'retirement-account'
    ? 'RetirementPersonalAccount'
    : 'InsurancePersonalAccount';
}

function getTierEnum(
  tier: 'primary' | 'contingent'
): BeneficiaryDesignationTier {
  return tier === 'primary'
    ? BeneficiaryDesignationTier.Primary
    : BeneficiaryDesignationTier.Contingent;
}

type CopyOptionEntityNode = NonNullable<
  NonNullable<
    NonNullable<
      CopyBeneficiaryDesignationsOptionsQuery['entities']['edges']
    >[number]
  >['node']
>;

/**
 * Narrows a queried entity to a BDG-bearing account subtype matching the
 * target type. Returns the subtype (with its display name + resolved
 * designations) or null.
 */
function getMatchingSubtype(
  entity: CopyOptionEntityNode,
  targetTypename: BdgAccountTypename
) {
  const { subtype } = entity;
  // Narrow to the two BDG-bearing account types first (both expose
  // `displayName` and the resolved `beneficiaryDesignations` field), then
  // require an exact match against the current entity's type.
  if (
    subtype.__typename !== 'RetirementPersonalAccount' &&
    subtype.__typename !== 'InsurancePersonalAccount'
  ) {
    return null;
  }
  if (subtype.__typename !== targetTypename) return null;
  return subtype;
}

type MatchingSubtype = NonNullable<ReturnType<typeof getMatchingSubtype>>;

/**
 * Picks the resolved beneficiary group for the requested tier. The resolver
 * live-converts legacy `dispositionScenarios`, so this returns designations for
 * un-migrated entities too (not just those with persisted BDG rows).
 */
function getTierGroup(
  subtype: MatchingSubtype,
  targetTier: BeneficiaryDesignationTier
) {
  const { primary, contingent } = subtype.beneficiaryDesignations;
  return targetTier === BeneficiaryDesignationTier.Primary
    ? primary
    : contingent;
}

interface UseCopyBeneficiaryDesignationOptionsParams {
  entityType: EntityType;
  tier: 'primary' | 'contingent';
  /** The entity being edited; excluded from the source list. */
  currentEntityId: string | null;
}

/**
 * Returns the list of same-type entities (excluding the current one) that have
 * beneficiaries on the matching tier and can be used as a copy source.
 */
export function useCopyBeneficiaryDesignationOptions({
  entityType,
  tier,
  currentEntityId,
}: UseCopyBeneficiaryDesignationOptionsParams) {
  const { householdId } = useHouseholdDetailsContext();

  const { data, ...queryProps } = useCopyBeneficiaryDesignationsOptionsQuery({
    variables: {
      entitiesLike: {
        hasHouseholdWith: [{ id: householdId }],
      },
    },
  });

  const targetTypename = getSubtypeTypename(entityType);
  const targetTier = getTierEnum(tier);

  const options: TypeaheadSelectInputOption<string>[] = flatMap(
    getNodes(data?.entities),
    (entity) => {
      if (entity.id === currentEntityId) return [];

      const subtype = getMatchingSubtype(entity, targetTypename);
      if (!subtype) return [];

      const tierGroup = getTierGroup(subtype, targetTier);
      if (!tierGroup || tierGroup.dispositiveProvisions.totalCount === 0) {
        return [];
      }

      return {
        display: subtype.displayName,
        value: entity.id,
      };
    }
  );

  return { options, data, targetTypename, targetTier, ...queryProps };
}

/**
 * Given the selected source entity id, returns the recipients copied from its
 * matching-tier beneficiary group. Recipients are decoupled from the source
 * provisions (`_dispositiveProvisionId: null`) so saving creates new ones.
 */
export function getCopyBeneficiaryDesignationsResult(
  sourceEntityId: string,
  targetTypename: BdgAccountTypename,
  targetTier: BeneficiaryDesignationTier,
  data: CopyBeneficiaryDesignationsOptionsQuery
): CopyBeneficiaryDesignationsResult | null {
  const entity = getNodes(data.entities).find((e) => e.id === sourceEntityId);
  if (!entity) return null;

  const subtype = getMatchingSubtype(entity, targetTypename);
  if (!subtype) return null;

  const tierGroup = getTierGroup(subtype, targetTier);
  if (!tierGroup) return null;

  const recipients = getNodes(tierGroup.dispositiveProvisions).map(
    (provision) => ({
      ...mapProvisionToRecipient(provision),
      _dispositiveProvisionId: null,
    })
  );

  // Return null (not an empty list) so the caller's `!result` guard catches an
  // empty source group instead of clearing the target tier.
  if (recipients.length === 0) return null;

  return { recipients };
}
